import * as moment from 'moment';
import { Moment } from 'moment';
import { TimeBlock } from 'src/models/changeover-review';

export default class LayoutHelper {
  private xScale: number;
  lastIndefiniteTimeBlock: TimeBlock | null = null;
  lowerBoundTime: Moment | null = null;
  now: moment.Moment = moment();
  constructor(
    xScale = 6.67,
    public xUpperBound = 1,
    public rowCount = 0,
    public readonly rowHeight = 46,
    public readonly timeBlockStrokeWidth = 2,
    public readonly timeDivisionIncrement = 10,
    public readonly rulerHeight = 20,
    public readonly rulerDivisionHeight = 10,
  ) {
    this.xScale = xScale;
  }

  get XScale(): number { return this.xScale; }
  set XScale(value: number) {
    this.xScale = Math.min(60, Math.max(3, value));
  }

  get rowHeightCss() { return `${this.rowHeight}px`; }

  get svgWidth(): number { return this.xUpperBound * this.XScale; }
  get svgHeight(): number { return this.rulerHeight + (this.rowCount * this.rowHeight); }
  get top(): number { return 0; }
  get bottom(): number { return this.rowCount * this.rowHeight; }
  get left(): number { return 0; }
  get right(): number { return this.xUpperBound * this.XScale; }
  get selectionStrokeWidth(): number { return this.timeBlockStrokeWidth * 2; }

  getCurrentTimeMarkerXPosition = () => this.now.diff(this.lowerBoundTime, 'minutes', true) * this.XScale;
  getStandardEndTimeMarkerXPosition = (offsetInMinutes: number) => offsetInMinutes * this.XScale;

  getTimeDivisionLineXPosition = (lineNumber: number) => (lineNumber) * this.timeDivisionIncrement * this.XScale;
  getRowBottomYPosition = (lineNumber: number) => (lineNumber + 1) * this.rowHeight - 0.5;

  getTimeBlockXPosition = (start: number) => (start * this.XScale) + (this.timeBlockStrokeWidth / 2);
  getTimeBlockYPosition = (lineNumber: number) => lineNumber * this.rowHeight + (this.timeBlockStrokeWidth / 2);
  getTimeBlockWidth = (block: TimeBlock) => Math.max(2, this.getBlockDuration(block) * this.XScale - this.timeBlockStrokeWidth);
  get timeBlockHeight(): number { return this.rowHeight - 1.5 * this.timeBlockStrokeWidth; }


  getSelectionOverlayYPosition = (lineNumber: number) => lineNumber * this.rowHeight + (this.selectionStrokeWidth / 3);
  getSelectionOverlayXPosition = (start: number) => (start * this.XScale) + this.timeBlockStrokeWidth;
  get SelectionOverlayHeight(): number { return this.rowHeight - (2 * this.timeBlockStrokeWidth); }
  getSelectionOverlayWidth = (block: TimeBlock) => Math.max(2, (this.getBlockDuration(block) * this.XScale) - (2 * this.timeBlockStrokeWidth));

  getOverdueOverlayYPosition = (lineNumber: number) => lineNumber * this.rowHeight + this.timeBlockStrokeWidth;
  getOverdueOverlayXPosition = (block: TimeBlock, standardEndOffsetInMinutes: number) => {
    const start = block.startOffsetInMinutes;
    const end = block.endOffsetInMinutes;
    const overlapsThreshold = start < standardEndOffsetInMinutes && end > standardEndOffsetInMinutes;

    if (overlapsThreshold) {
      return this.xScale * standardEndOffsetInMinutes;
    }
    return this.getSelectionOverlayXPosition(start);
  }
  get OverdueOverlayHeight(): number { return this.rowHeight - (2.5 * this.timeBlockStrokeWidth); }
  getOverdueOverlayWidth = (block: TimeBlock, standardEndOffsetInMinutes: number) => {
    const start = block.startOffsetInMinutes;
    const end = block.endOffsetInMinutes;

    const fullyOverdue = start > standardEndOffsetInMinutes;
    const partiallyOverdue = start < standardEndOffsetInMinutes && end > standardEndOffsetInMinutes

    if (fullyOverdue) {
      return this.getSelectionOverlayWidth(block);
    }
    if (partiallyOverdue) {
      return Math.max(2, (this.xScale * (block.endOffsetInMinutes - standardEndOffsetInMinutes)) - this.timeBlockStrokeWidth);
    }
    return 0;
  }

  public readonly iconSize = 16;
  private iconXOffset = 4;
  private iconYOffset = 4;
  getIconXPosition = (block: TimeBlock) => this.getTimeBlockXPosition(block.startOffsetInMinutes) + this.iconXOffset;
  getIconYPosition = (lineNumber: number) => this.getTimeBlockYPosition(lineNumber) + this.iconYOffset;
  getIconClipPath = (block: TimeBlock) => {
    const xPosition = this.getTimeBlockWidth(block) - this.iconXOffset - (0.5 * this.timeBlockStrokeWidth);
    return `polygon(0 0, ${xPosition} 0, ${xPosition} 100%, 0 100%)`;
  }

  getFillName = (block: TimeBlock) =>
    block.isIndefinite
      ? (block.isActive
        ? 'activeIndefiniteFill'
        : 'inactiveIndefiniteFill')
      : (block.isActive
        ? 'activeFill'
        : 'inactiveFill');
  getStrokeDefinition = (block: TimeBlock) =>
    block.isIndefinite
      ? (block.isActive
        ? 'activeIndefiniteStroke'
        : 'inactiveIndefiniteStroke')
      : (block.isActive
        ? 'activeStroke'
        : 'inactiveStroke');

  private getBlockDuration = (block: TimeBlock) => block.isIndefinite ? this.getIndefiniteDuration(block) : block.durationInMinutes;

  private getIndefiniteDuration = (block: TimeBlock): number => {
    const finalBlock = this.lastIndefiniteTimeBlock!;
    const finalBlockStartTime = moment(finalBlock.startTime);
    const finalBlockWidth = this.now.diff(finalBlockStartTime, 'minutes', true);

    const endTime = finalBlockStartTime.add(finalBlockWidth, 'minutes');

    const duration = endTime.diff(moment(block.startTime), 'minutes', true);
    return duration;
  }
}
